// src/components/DesignRulesModal.jsx - Modal to show passed/failed design rules
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { CheckCircle, XCircle, AlertTriangle, Info, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

const DesignRulesModal = ({ isOpen, onClose, designQuality, slideNumber }) => {
  const [showAllPassed, setShowAllPassed] = useState(false);
  const [showAllFailed, setShowAllFailed] = useState(true);

  if (!designQuality || !designQuality.rule_evaluation) {
    return null;
  }

  const { rules_passed, violations } = designQuality;
  const ruleEval = designQuality.rule_evaluation;

  // Get all rules and their results
  const allRules = ruleEval.rules_checked || [];
  const passedRules = allRules.filter(rule => rule.passed);
  const failedRules = allRules.filter(rule => !rule.passed);

  // Group failed rules by priority
  const highPriority = failedRules.filter(r => r.severity === 'high' || r.priority === 'high');
  const mediumPriority = failedRules.filter(r => r.severity === 'medium' || r.priority === 'medium');
  const lowPriority = failedRules.filter(r => r.severity === 'low' || r.priority === 'low');

  const RuleItem = ({ rule, passed }) => {
    const Icon = passed ? CheckCircle : XCircle;
    const iconColor = passed ? 'text-green-600' : 'text-red-600';
    const bgColor = passed ? 'bg-green-50' : 'bg-red-50';
    const borderColor = passed ? 'border-green-200' : 'border-red-200';

    return (
      <div className={`${bgColor} ${borderColor} border rounded-lg p-4 space-y-2`}>
        <div className="flex items-start gap-3">
          <Icon className={`w-5 h-5 ${iconColor} flex-shrink-0 mt-0.5`} />
          <div className="flex-1">
            <div className="flex items-center justify-between mb-1">
              <h4 className="text-sm font-semibold text-gray-900">{rule.rule_name}</h4>
              {rule.points && (
                <Badge variant="outline" className="text-xs">
                  {rule.points} pts
                </Badge>
              )}
            </div>

            {rule.suggestion && (
              <p className="text-xs text-gray-700 mb-2">
                💡 {rule.suggestion}
              </p>
            )}

            {rule.current_state && (
              <p className="text-xs text-gray-600 italic">
                📊 Current: {rule.current_state}
              </p>
            )}

            {rule.details && (
              <p className="text-xs text-gray-500 mt-1">
                {rule.details}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  const PrioritySection = ({ title, rules, icon: Icon, color }) => {
    if (!rules || rules.length === 0) return null;

    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Icon className={`w-4 h-4 ${color}`} />
          <h4 className="text-sm font-semibold text-gray-700">{title}</h4>
          <Badge variant="outline" className="ml-auto">{rules.length}</Badge>
        </div>
        <div className="space-y-2">
          {rules.map((rule, idx) => (
            <RuleItem key={idx} rule={rule} passed={false} />
          ))}
        </div>
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <span>Design Quality Rules</span>
            {slideNumber && (
              <Badge variant="secondary">Slide {slideNumber}</Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            Professional design principles analysis - {rules_passed || '0/0'} rules passed
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Passed Rules Section */}
          <div className="space-y-3">
            <button
              onClick={() => setShowAllPassed(!showAllPassed)}
              className="w-full flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors"
            >
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="font-semibold text-green-900">
                  PASSED RULES ({passedRules.length}/{allRules.length})
                </span>
              </div>
              {showAllPassed ? (
                <ChevronUp className="w-5 h-5 text-green-600" />
              ) : (
                <ChevronDown className="w-5 h-5 text-green-600" />
              )}
            </button>

            {showAllPassed && passedRules.length > 0 && (
              <div className="space-y-2 pl-4">
                {passedRules.map((rule, idx) => (
                  <RuleItem key={idx} rule={rule} passed={true} />
                ))}
              </div>
            )}

            {showAllPassed && passedRules.length === 0 && (
              <div className="text-sm text-gray-600 text-center py-4 bg-gray-50 rounded-lg">
                No rules passed yet. Check recommendations below for improvements.
              </div>
            )}
          </div>

          {/* Failed Rules Section */}
          <div className="space-y-3">
            <button
              onClick={() => setShowAllFailed(!showAllFailed)}
              className="w-full flex items-center justify-between p-3 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
            >
              <div className="flex items-center gap-3">
                <XCircle className="w-5 h-5 text-red-600" />
                <span className="font-semibold text-red-900">
                  FAILED RULES ({failedRules.length}/{allRules.length})
                </span>
              </div>
              {showAllFailed ? (
                <ChevronUp className="w-5 h-5 text-red-600" />
              ) : (
                <ChevronDown className="w-5 h-5 text-red-600" />
              )}
            </button>

            {showAllFailed && failedRules.length > 0 && (
              <div className="space-y-4 pl-4">
                <PrioritySection
                  title="HIGH PRIORITY"
                  rules={highPriority}
                  icon={AlertTriangle}
                  color="text-red-600"
                />
                <PrioritySection
                  title="MEDIUM PRIORITY"
                  rules={mediumPriority}
                  icon={Info}
                  color="text-yellow-600"
                />
                <PrioritySection
                  title="LOW PRIORITY"
                  rules={lowPriority}
                  icon={Info}
                  color="text-blue-600"
                />
              </div>
            )}

            {showAllFailed && failedRules.length === 0 && (
              <div className="text-sm text-green-700 text-center py-4 bg-green-50 rounded-lg">
                🎉 All rules passed! This slide follows professional design principles.
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DesignRulesModal;
